// PDFReportDocument.js
import React, { Fragment } from "react";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import { formatPdfFooter, formatPdfTotal } from "../../../reportExport";
import {
  PDF_PAGE_PADDING,
  CELL_PADDING_RIGHT,
  ROW_PADDING_VERTICAL,
  KEEP_WHOLE_MAX_HEIGHT,
  DETAIL_LINE_LABELS,
  DETAIL_LINE_FONT_SIZE,
  DETAIL_LABEL_PADDING_LEFT,
  DETAIL_VALUE_PADDING_LEFT,
  DETAIL_LINE_PADDING_BOTTOM,
  estimateDetailLineHeight,
  shouldKeepRowWhole,
} from "../../../pdfReportGeneration/pdfColumnWidths";

const PDFReportDocument = ({
  title,
  headings,
  data,
  columnWidth,
  startDate,
  endDate,
  variableColumnWidthsArray,
  columnWidthTypeRadioValue,
  headingsAndDataFontSize,
  sumAndAvgFontSize,
  columnSums,
  columnAverages,
  showSUMRow,
  showAVGRow,
  headingsSpacing,
  dataSpacing,
  isDownload,
  /* JFCRX-7643. Set by the viewer in PREVIEW mode only: the preview is one
   * ten-row document per pager page, so react-pdf's counter below would
   * always read "1 / 1". When given, it replaces that counter (see
   * formatPdfFooter). The download passes nothing and keeps the real page
   * numbers. */
  footerLabel,
  /* JFCRX-7754. Preview only, alongside footerLabel: called with the number
   * of sheets this ten-row document rendered to, so the viewer can say when
   * the preview page continues below the visible sheet. */
  onSheetCount,
}) => {
  const ROWS_PER_PDF_PAGE = 40;

  /* Where a row splits, and it is load-bearing in two places: the ten values
   * the table row draws as cells, and the five the detail line draws beneath
   * it as label/value pairs. Ten cells plus five pairs is exactly where
   * PDF_TEXT_NODES_PER_SUBSTITUTION_ROW = 20 in Reports2/reportExport.js comes
   * from, so moving this boundary also moves the PDF export cap (JFCRX-6749).
   *
   * Named because they must stay complementary. Raise the first without the
   * second and a value is drawn twice; lower it and one disappears -- with no
   * error either way, which is the same silent-misalignment failure as the
   * defect this function was rewritten to remove.
   */
  const MAIN_ROW_CELLS = 10;
  const DETAIL_LINE_FIELDS = DETAIL_LINE_LABELS.length;

  const pages = [];

  /* The width of a data cell in column `i` for the selected width mode. The
   * Sum and Average cells use it too (JFCRX-7652), so a total sits under its
   * own column in every mode, not only in Variable Widths. */
  const dataCellWidth = (i) =>
    columnWidthTypeRadioValue === 1
      ? variableColumnWidthsArray[i]
      : columnWidthTypeRadioValue === 2
        ? columnWidth
        : dataSpacing[i].columnWidth;

  /* JFCRX-7652. Only totals that have a column are drawn, matching what the
   * width calculation measures. The viewer passes one total per report field
   * (15) for 10 headings, and the 5 extra "--" cells had no width, so
   * flexShrink squeezed the whole row off its columns whenever a Sum or
   * Average row showed. */
  const totalsCells = (totals) =>
    totals.slice(0, headings.length).map((total, i) => (
      <Text
        key={i}
        style={{
          width: dataCellWidth(i),
          fontSize: sumAndAvgFontSize,
          paddingRight: CELL_PADDING_RIGHT,
        }}
      >
        {formatPdfTotal(total)}
      </Text>
    ));

  /* JFCRX-6781. Every row carries its OWN detail values from here on.
   *
   * The detail line used to read data[rIdx], where rIdx is the row's index
   * WITHIN ITS PAGE, not within the report. Page one happened to be right
   * because it starts at zero; from page two onwards every row printed a
   * DIFFERENT record's Rx number, patient name and order id beneath its drug
   * -- 40 records out on page two, 80 on page three.
   *
   * Pairing the two halves of a row before anything is paged removes the
   * possibility rather than correcting the arithmetic, so no later change to
   * how rows are sliced, paged or ordered can reintroduce it. That matters
   * here: JFCRX-6782 changes what `data` holds for grouped reports, and this
   * stays correct through it.
   */
  const pageableRows = data.map((dataRow) => {
    const details = dataRow.slice(
      MAIN_ROW_CELLS,
      MAIN_ROW_CELLS + DETAIL_LINE_FIELDS,
    );
    // JFCRX-7755. For the keep-whole estimates below.
    return {
      cells: dataRow.slice(0, MAIN_ROW_CELLS),
      details,
      detailHeight: estimateDetailLineHeight(details),
    };
  });

  const headerView = (
    <View
      fixed
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 10,
        fontWeight: 500,
        marginBottom: 10,
      }}
    >
      <Text>{title}</Text>
      <Text>{startDate && endDate ? `${startDate} - ${endDate}` : ""}</Text>
    </View>
  );

  const headingsRow = (
    <View
      fixed
      style={{
        flexDirection: "row",
        borderBottom: "2px solid #089BAB",
        paddingVertical: 12,
      }}
    >
      {headings.map((heading, i) => {
        let width =
          columnWidthTypeRadioValue === 1
            ? variableColumnWidthsArray[i]
            : columnWidthTypeRadioValue === 2
              ? columnWidth
              : headingsSpacing[i].columnWidth;

        return (
          <Text
            key={i}
            style={{
              width,
              fontSize: headingsAndDataFontSize,
              fontWeight: 500,
              paddingRight: CELL_PADDING_RIGHT,
            }}
          >
            {heading}
          </Text>
        );
      })}
    </View>
  );

  /* JFCRX-7643. At least one page, so a report with no rows still shows its
   * header, headings and footer rather than a blank viewer. */
  const pageCount = Math.max(
    1,
    Math.ceil(pageableRows.length / ROWS_PER_PDF_PAGE),
  );
  for (let p = 0; p < pageCount; p += 1) {
    const i = p * ROWS_PER_PDF_PAGE;
    const pageRows = pageableRows.slice(i, i + ROWS_PER_PDF_PAGE);
    const isLastPage = p === pageCount - 1;

    pages.push(
      <Page
        key={i}
        size="LETTER"
        orientation="landscape"
        style={{ padding: PDF_PAGE_PADDING, fontFamily: "Poppins" }}
      >
        {headerView}
        {headingsRow}
        {pageRows.map((row, rIdx) => {
          /* JFCRX-6781 (QA 2026-10-30). wrap={false} keeps a row's cells and
           * its detail line on the same physical page. Pages are height-based
           * and wrap by default, and ROWS_PER_PDF_PAGE rows never fit on one,
           * so without this react-pdf split the container between its two
           * children: the drug row closed one page and its Rx #, patient name
           * and order id opened the next under a fresh header. The preview
           * can show it too, when a preview page runs onto a second sheet
           * (JFCRX-7754).
           *
           * JFCRX-7755. But a wrap={false} row taller than the page is drawn
           * past the page's bottom edge and its text is lost, so the row is
           * kept whole only when its estimated height, detail line included,
           * is at most KEEP_WHOLE_MAX_HEIGHT, two thirds of a page
           * (shouldKeepRowWhole; ROW HEIGHT in pdfColumnWidths.js). A taller
           * row stays splittable: its cells break by line (orphans and
           * widows of 1) in Views that keep each column in place, and all
           * its text is drawn. Its detail line may then open the next page,
           * but is itself kept whole unless it alone is taller than the
           * limit: unlike the cells, its pairs have no columns to hold them,
           * so a value split from its label would continue at the left
           * edge. */
          const splittable = !shouldKeepRowWhole({
            cells: row.cells,
            widths: row.cells.map((_, cIdx) => dataCellWidth(cIdx)),
            fontSize: headingsAndDataFontSize,
            extraHeight: row.detailHeight,
          });
          return (
            <View key={rIdx} wrap={splittable}>
              <View
                style={{
                  flexDirection: "row",
                  backgroundColor: rIdx % 2 ? "#EBEDF8" : "#FFFFFF",
                  paddingVertical: ROW_PADDING_VERTICAL,
                }}
              >
                {row.cells.map((cell, cIdx) => {
                  const text = (
                    <Text
                      key={cIdx}
                      orphans={1}
                      widows={1}
                      style={{
                        width: dataCellWidth(cIdx),
                        fontSize: headingsAndDataFontSize,
                        paddingRight: CELL_PADDING_RIGHT,
                      }}
                    >
                      {/* JFCRX-7651. Drawn as given. Dashes used to be
                          swapped for soft hyphens, and textkit drops the dash
                          at a soft-hyphen break ("50111-0788" / "01"); the
                          width module also measures the text as given.
                          Production has drawn cells as given since e8544c712. */}
                      {cell}
                    </Text>
                  );
                  // JFCRX-7755. See CELL VIEWS ON SPLITTABLE ROWS in
                  // pdfColumnWidths.js.
                  return splittable ? (
                    <View key={cIdx} style={{ width: dataCellWidth(cIdx) }}>
                      {text}
                    </View>
                  ) : (
                    text
                  );
                })}
              </View>
              {/* JFCRX-6749. This detail line is five LABEL AND VALUE pairs --
                  ten <Text> nodes -- on top of the ten cells in the row above,
                  which is why the PDF export cap is spent at 20 nodes per row
                  and not at the report's 15 columns. If a pair is added to or
                  removed from DETAIL_LINE_LABELS (pdfColumnWidths.js), update
                  PDF_TEXT_NODES_PER_SUBSTITUTION_ROW in
                  Reports2/reportExport.js or the cap silently stops matching
                  what this draws. JFCRX-7755: its font, paddings and labels
                  come from pdfColumnWidths.js, which estimates its height
                  from the same values. */}
              <View
                wrap={!(row.detailHeight <= KEEP_WHOLE_MAX_HEIGHT)}
                style={{
                  display: "flex",
                  // flexWrap: "wrap",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  backgroundColor: rIdx % 2 ? "#EBEDF8" : "#ffffff",
                  paddingBottom: DETAIL_LINE_PADDING_BOTTOM,
                }}
              >
                {DETAIL_LINE_LABELS.flatMap((label, i) => [
                  <Text
                    key={`label-${i}`}
                    orphans={1}
                    widows={1}
                    style={{
                      fontSize: DETAIL_LINE_FONT_SIZE,
                      fontWeight: 500,
                      paddingLeft: DETAIL_LABEL_PADDING_LEFT,
                    }}
                  >
                    {label}
                  </Text>,
                  <Text
                    key={`value-${i}`}
                    orphans={1}
                    widows={1}
                    style={{
                      fontSize: DETAIL_LINE_FONT_SIZE,
                      fontWeight: 300,
                      paddingLeft: DETAIL_VALUE_PADDING_LEFT,
                    }}
                  >
                    {row.details[i]}
                  </Text>,
                ])}
              </View>
            </View>
          );
        })}
        <Text
          fixed
          style={{
            position: "absolute",
            bottom: 6,
            width: "100%",
            textAlign: "center",
            fontSize: 10,
          }}
          render={({ pageNumber, totalPages }) => {
            /* JFCRX-7754. react-pdf calls this twice per page, and the first
             * pass has no totalPages yet, hence the guard. */
            if (footerLabel && onSheetCount && Number.isFinite(totalPages)) {
              onSheetCount(totalPages);
            }
            return formatPdfFooter({ footerLabel, pageNumber, totalPages });
          }}
        />

        {/* JFCRX-7755. Each label is kept on one page with its totals; the
            pair is a few lines at most, far under a page. */}
        {isDownload && isLastPage && (
          <Fragment>
            {showSUMRow && (
              <View wrap={false}>
                <Text
                  style={{
                    fontSize: sumAndAvgFontSize,
                    fontWeight: 500,
                    color: "#089BAB",
                    paddingTop: 12,
                  }}
                >
                  Sum
                </Text>
                <View style={{ flexDirection: "row", paddingTop: 10 }}>
                  {totalsCells(columnSums)}
                </View>
              </View>
            )}
            {showAVGRow && (
              <View wrap={false}>
                <Text
                  style={{
                    fontSize: sumAndAvgFontSize,
                    fontWeight: 500,
                    color: "#089BAB",
                    paddingTop: 12,
                  }}
                >
                  Average
                </Text>
                <View style={{ flexDirection: "row", paddingTop: 10 }}>
                  {totalsCells(columnAverages)}
                </View>
              </View>
            )}
          </Fragment>
        )}
      </Page>,
    );
  }

  return <Document title={title}>{pages}</Document>;
};

export default PDFReportDocument;
